import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_356: DocMigration = {
  maxSchemaVersion: "0.0.356",
  description: "Align viewport and view template properties",
  apply(json: any) {
    if (!json.sectionViews) {
      json.sectionViews = {};
    }

    if (!json.viewTemplates) {
      json.viewTemplates = {};
    }

    // add a section view if one doesn't exist
    if (Object.keys(json.sectionViews).length === 0) {
      const id = uid();
      const newSectionView = {
        id,
        entityType: "SECTION_VIEW",
        name: "Section View",
        sortIndex: 0,
        startZ: -36,
        endZ: 300,
        startX: -48,
        startY: 24,
        endX: 240,
        endY: 24,
      };
      json.sectionViews[id] = newSectionView;
    }

    const docHasViewTemplate = (viewType: string) => {
      return Object.keys(json.viewTemplates).some((id) => json.viewTemplates[id].viewType === viewType);
    };

    const elevationViewIdFromReferenceFrame = (referenceFrame: string) => {
      return Object.keys(json.elevationViews).find((id) => json.elevationViews[id].referenceFrame === referenceFrame);
    };

    // add view templates of all types if they don't exist
    if (!docHasViewTemplate("PLAN_VIEW")) {
      const id = uid();
      const newPlanViewTemplate = {
        id,
        viewType: "PLAN_VIEW",
        entityType: "VIEW_TEMPLATE",
        name: "Plan View Template",
        sortIndex: 0,
        referenceType: "FLOOR",
        // Copied: later migrations splice these arrays in place, so handing out the module-level one
        // lets one parse reorder it for every parse after.
        categories: [...defaultFloorPlanCategories],
        viewTemplateOverrideIds: [],
      };
      json.viewTemplates[id] = newPlanViewTemplate;
    }

    if (!docHasViewTemplate("ELEVATION_VIEW")) {
      const id = uid();
      const newElevationViewTemplate = {
        id,
        viewType: "ELEVATION_VIEW",
        entityType: "VIEW_TEMPLATE",
        name: "Elevation View Template",
        sortIndex: 0,
        belowGradeRenderStyle: "NONE",
        categories: [...defaultElevationCategories],
        viewTemplateOverrideIds: [],
      };
      json.viewTemplates[id] = newElevationViewTemplate;
    }

    if (!docHasViewTemplate("SECTION_VIEW")) {
      const id = uid();
      const newSectionViewTemplate = {
        id,
        viewType: "SECTION_VIEW",
        entityType: "VIEW_TEMPLATE",
        name: "Section View Template",
        sortIndex: 0,
        categories: [...defaultSectionCategories],
        viewTemplateOverrideIds: [],
      };
      json.viewTemplates[id] = newSectionViewTemplate;
    }

    if (!docHasViewTemplate("VIEW_3D")) {
      const id = uid();
      const newView3DTemplate = {
        id,
        viewType: "VIEW_3D",
        entityType: "VIEW_TEMPLATE",
        name: "3D View Template",
        sortIndex: 0,
        categories: [],
        viewTemplateOverrideIds: [],
      };
      json.viewTemplates[id] = newView3DTemplate;
    }

    const referencedViewTemplateIds = {};

    for (const viewportId in json.viewports) {
      const viewport = json.viewports[viewportId];
      const viewTemplate = json.viewTemplates[viewport.viewTemplateId];

      // if viewport.viewTemplateId is a parameter or undefined, we can't determine the view type
      // from the view template and need to choose reasonable defaults
      if (!viewTemplate) {
        viewport.viewType = "PLAN_VIEW";
        viewport.contentSourceId = viewport.levelId ?? Object.keys(json.levels)[0];
        delete viewport.levelId;
        viewport.viewTemplateId = Object.keys(json.viewTemplates).filter(
          (id) => json.viewTemplates[id].viewType === "PLAN_VIEW",
        )[0];
        continue;
      }

      referencedViewTemplateIds[viewTemplate.id] = true;
      viewport.viewType = viewTemplate.viewType;

      switch (viewport.viewType) {
        case "PLAN_VIEW":
          viewport.contentSourceId = viewport.levelId ?? Object.keys(json.levels)[0];
          delete viewport.levelId;
          break;
        case "SECTION_VIEW":
          viewport.contentSourceId =
            viewport.sectionViewId ?? viewTemplate.sectionViewId ?? Object.keys(json.sectionViews)[0];
          delete viewport.sectionViewId;
          delete viewTemplate.sectionViewId;
          break;
        case "ELEVATION_VIEW":
          viewport.contentSourceId = elevationViewIdFromReferenceFrame(viewTemplate.referenceFrame ?? "FRONT");
          delete viewTemplate.referenceFrame;
          break;
        case "VIEW_3D":
          viewport.contentSourceId = viewTemplate.cameraKey ?? "defaultExteriorCamera";
          delete viewTemplate.cameraKey;
          break;
        default:
          break;
      }
    }

    // clean up view templates that aren't referenced by viewports
    for (const viewTemplateId in json.viewTemplates) {
      if (referencedViewTemplateIds[viewTemplateId]) {
        continue;
      }

      const viewTemplate = json.viewTemplates[viewTemplateId];

      if (viewTemplate.sectionViewId) {
        delete viewTemplate.sectionViewId;
      }

      if (viewTemplate.cameraKey) {
        delete viewTemplate.cameraKey;
      }

      if (viewTemplate.referenceFrame) {
        delete viewTemplate.referenceFrame;
      }
    }

    // add sub types to view template and option choice parameters
    // these only restrict option choice and view type selection in the UI and won't prevent viewport content from being generated
    // in the case of a mismatch
    for (const parameterId in json.parameters) {
      const parameter = json.parameters[parameterId];

      if (parameter.type === "VIEW_TEMPLATE" || parameter.type === "CONTENT_SOURCE") {
        parameter.viewType = "PLAN_VIEW";
      }

      if (parameter.type === "OPTION_CHOICE") {
        parameter.optionSetId = "elevationStyle";
      }
    }

    return json;
  },
};

const defaultFloorPlanCategories: string[] = [
  "SLAB_PATIO",
  "RAILING",
  "RAILING_POST",
  "PROPS",
  "PROP_TAG",
  "PORCH_COLUMNS",
  "PORCH_BEAMS",
  "PORCH",
  "DECK",
  "PERIMS_WALL",
  "WALLS",
  "WALL_ASSEMBLIES",
  "CHASE_ROOMS",
  "STEM_WALL_UNIONED_EDGES",
  "PIER_EDGES_ONLY",
  "STAIR",
  "RAILING_STAIR_ONLY",
  "RAILING_POST_STAIR_ONLY",
  "CASEWORK",
  "CEILING",
  "WAINSCOTING",
  "DOORS",
  "WINDOWS",
  "COLUMN",
  "WALL_EXTERIOR_DIMENSIONS",
  "WALL_EXTERIOR_DIM_LINEWORK",
  "WALL_EXTERIOR_DIM_TICK",
  "WALL_EXTERIOR_DIM_LABEL",
  "WALL_INTERIOR_DIMENSIONS",
  "WALL_INTERIOR_DIM_LINEWORK",
  "WALL_INTERIOR_DIM_TICK",
  "WALL_INTERIOR_DIM_LABEL",
  "ROOM_LABELS",
  "CASEWORK_LABELS",
  "WINDOW_TAGS",
  "DOOR_TAGS",
  "ANNOTATION",
  "ANNOT_LINEWORK_FILL",
  "ANNOT_LINEWORK_LINE",
  "ANNOT_LINEWORK_OFFSETLINE",
  "ANNOT_NOTE",
  "ANNOT_DIM_LABEL",
  "ANNOT_DIM_LINEWORK",
  "ANNOT_DIM_WITNESS",
  "ANNOT_DIM_TICK",
];

const defaultElevationCategories: string[] = [
  "ELEVATION_LINEWORK_3D",
  "ANNOTATION",
  "ELEVATION_MARKERS",
  "MATERIAL_TAGS",
  "GRADE_LINE",
  "TOP_OF_PLATE_ANNOTATION",
  "DORMER_WINDOW_VERTICAL_DIMENSIONS",
  "DORMER_WINDOW_TAGS",
  "ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS",
  "ROOF_GABLE_WINDOW_TAGS",
  "ROOF_DIM_ANNOTATION",
  "ROOF_SLOPE_SYMBOL",
  "WINDOW_HEADER_ANNOTATION",
];

const defaultSectionCategories: string[] = [
  "SECTION_LINEWORK_3D",
  "ANNOTATION",
  "ANNOT_LINEWORK_FILL",
  "ANNOT_LINEWORK_LINE",
  "ANNOT_LINEWORK_OFFSETLINE",
  "ANNOT_NOTE",
  "ANNOT_DIM_LABEL",
  "ANNOT_DIM_LINEWORK",
  "ANNOT_DIM_WITNESS",
  "ANNOT_DIM_TICK",
];

export default migration_0_0_356;
